<div
  class="app-data-table"
  [class.app-data-table--loading]="loading"
  [class.app-data-table--compact]="compact"
  [attr.data-testid]="testId"
>
  <span
    *ngIf="loading"
    class="app-data-table__loading-announcement"
    role="status"
  >
    Loading {{ ariaLabel }}
  </span>
  <div class="app-data-table__scroll">
    <table
      class="app-data-table__grid"
      [attr.aria-label]="ariaLabel"
      [attr.aria-busy]="loading"
    >
      <thead>
        <tr [style.grid-template-columns]="columnTemplate">
          <th
            *ngFor="let column of columns; let columnIndex = index"
            scope="col"
            class="app-data-table__cell"
            [class.app-data-table__cell--wrap]="column.wrap"
            [class.app-data-table__cell--end]="column.align === 'end'"
            [class.app-data-table__cell--center]="column.align === 'center'"
            [attr.aria-sort]="getAriaSort(column)"
          >
            <button
              *ngIf="column.sortable; else plainHeaderLabel"
              type="button"
              class="app-data-table__sort-button"
              [disabled]="loading"
              (click)="onHeaderClick(column)"
            >
              <span class="app-data-table__sort-label">{{ column.label }}</span>
              <mat-icon
                *ngIf="!column.tooltip"
                aria-hidden="true"
                class="app-data-table__sort-icon"
                >{{ getSortIcon(column) }}</mat-icon
              >
            </button>
            <ng-template #plainHeaderLabel>{{ column.label }}</ng-template>
            <app-info-icon
              *ngIf="column.tooltip"
              class="app-data-table__header-info"
              [label]="'About ' + column.label"
              [tooltip]="column.tooltip"
              tooltipPosition="above"
            ></app-info-icon>
            <!-- With an info button, the sort icon follows it; the label button stays the keyboard control. -->
            <button
              *ngIf="column.sortable && column.tooltip"
              type="button"
              class="app-data-table__sort-button app-data-table__sort-icon-button"
              tabindex="-1"
              aria-hidden="true"
              [disabled]="loading"
              (click)="onHeaderClick(column)"
            >
              <mat-icon class="app-data-table__sort-icon">{{
                getSortIcon(column)
              }}</mat-icon>
            </button>
            <button
              *ngIf="
                !loading && resizableColumns && columnIndex < columns.length - 1
              "
              type="button"
              class="app-data-table__resize-handle"
              [attr.aria-label]="'Resize ' + column.label + ' column'"
              [attr.title]="
                'Drag to resize ' +
                column.label +
                '; use arrow keys when focused'
              "
              (pointerdown)="onResizeStart($event, columnIndex)"
              (pointermove)="onResizeMove($event)"
              (pointerup)="onResizeEnd($event)"
              (pointercancel)="onResizeEnd($event)"
              (keydown)="onResizeKeyDown($event, columnIndex)"
            >
              <mat-icon aria-hidden="true">drag_handle</mat-icon>
            </button>
          </th>
        </tr>
      </thead>
      <tbody #scrollRegion>
        <ng-container *ngIf="loading">
          <tr
            *ngFor="let rowIndex of skeletonRows; trackBy: trackByPage"
            class="app-data-table__skeleton-row"
            [style.grid-template-columns]="columnTemplate"
            aria-hidden="true"
          >
            <td *ngFor="let column of columns">
              <app-skeleton class="app-data-table__cell-skeleton" />
            </td>
          </tr>
        </ng-container>
        <ng-container *ngIf="!loading">
          <tr
            *ngFor="let item of displayRows; trackBy: trackByDisplayRow"
            class="app-data-table__row"
            [attr.id]="isTree ? item.id : null"
            [style.grid-template-columns]="columnTemplate"
            [class.app-data-table__row--clickable]="
              rowClickable && !isGroupRow(item)
            "
            [class.app-data-table__row--group]="isGroupRow(item)"
            [class.app-data-table__row--child]="item.level > 0"
            [class.app-data-table__row--selected]="
              !isGroupRow(item) && isRowSelected(item.row)
            "
            [attr.aria-current]="
              !isGroupRow(item) && isRowSelected(item.row) ? 'true' : null
            "
            [attr.data-testid]="rowTestId"
            (click)="onRowClick(item)"
          >
            <td
              *ngFor="let column of columns; let columnIndex = index"
              class="app-data-table__cell"
              [class.app-data-table__cell--wrap]="column.wrap"
              [class.app-data-table__cell--end]="column.align === 'end'"
              [class.app-data-table__cell--center]="column.align === 'center'"
              [attr.role]="column.key === rowHeaderKey ? 'rowheader' : null"
            >
              <ng-template #cellContent>
                <ng-container
                  *ngIf="
                    getCellTemplate(column.key) as cellTemplate;
                    else plainCell
                  "
                >
                  <ng-container
                    *ngTemplateOutlet="
                      cellTemplate;
                      context: {
                        $implicit: item.row,
                        row: item.row,
                        level: item.level,
                        parent: item.parent,
                      }
                    "
                  ></ng-container>
                </ng-container>
                <ng-template #plainCell>{{
                  $any(item.row)[column.key]
                }}</ng-template>
              </ng-template>

              @if (isTree && columnIndex === 0) {
                <div class="app-data-table__tree-cell">
                  @if (item.level === 0 && item.hasChildren) {
                    <button
                      type="button"
                      class="app-data-table__tree-toggle"
                      [attr.aria-expanded]="item.expanded"
                      [attr.aria-controls]="item.childIds"
                      [attr.aria-label]="item.toggleLabel"
                      (click)="toggleRow(item, $event)"
                    >
                      <mat-icon aria-hidden="true">{{
                        item.expanded ? 'expand_more' : 'chevron_right'
                      }}</mat-icon>
                    </button>
                  } @else {
                    <span
                      class="app-data-table__tree-spacer"
                      [class.app-data-table__tree-spacer--branch]="
                        item.level > 0
                      "
                      [class.app-data-table__tree-spacer--last]="item.lastChild"
                      aria-hidden="true"
                    ></span>
                  }
                  <div class="app-data-table__tree-content">
                    <ng-container
                      *ngTemplateOutlet="cellContent"
                    ></ng-container>
                  </div>
                </div>
              } @else {
                <ng-container *ngTemplateOutlet="cellContent"></ng-container>
              }
            </td>
          </tr>
        </ng-container>
      </tbody>
    </table>
  </div>

  <div *ngIf="showFooter" class="app-data-table__footer">
    <div class="app-data-table__footer-meta app-data-table__page-size">
      <span>Rows per page</span>
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-select
          aria-label="Rows per page"
          [value]="pageSize"
          [disabled]="loading"
          (selectionChange)="onPageSizeChange($event)"
        >
          <mat-option *ngFor="let size of pageSizeOptions" [value]="size">
            {{ size }}
          </mat-option>
        </mat-select>
      </mat-form-field>
    </div>
    <span class="app-data-table__footer-meta app-data-table__page-count">
      @if (loading) {
        Loading results
      } @else {
        <strong>{{ totalItems > 0 ? rangeStart + '–' + rangeEnd : 0 }}</strong>
        of
        {{ totalItems }}
      }
    </span>

    <div class="app-data-table__pagination">
      <button
        *ngIf="!compactPagination"
        type="button"
        class="app-data-table__page-link app-data-table__page-link--step app-data-table__page-link--first"
        aria-label="First page"
        [disabled]="loading || currentPage <= 1"
        (click)="onFirst()"
      >
        First
      </button>
      <button
        type="button"
        class="app-data-table__page-link app-data-table__page-link--step app-data-table__page-link--icon"
        aria-label="Previous page"
        title="Previous page"
        [disabled]="loading || currentPage <= 1"
        (click)="onPrevious()"
      >
        <mat-icon aria-hidden="true">chevron_left</mat-icon>
      </button>
      <button
        type="button"
        *ngFor="
          let page of compactPagination ? [] : pageLinks;
          trackBy: trackByPage
        "
        class="app-data-table__page-link"
        [class.app-data-table__page-link--active]="page === currentPage"
        [attr.aria-current]="page === currentPage ? 'page' : null"
        [disabled]="loading || totalPages <= 1"
        (click)="onPageClick(page)"
      >
        {{ page }}
      </button>
      <button
        type="button"
        class="app-data-table__page-link app-data-table__page-link--step app-data-table__page-link--icon app-data-table__page-link--next"
        aria-label="Next page"
        title="Next page"
        [disabled]="loading || currentPage >= totalPages"
        (click)="onNext()"
      >
        <mat-icon aria-hidden="true">chevron_right</mat-icon>
      </button>
      <button
        *ngIf="!compactPagination"
        type="button"
        class="app-data-table__page-link app-data-table__page-link--step app-data-table__page-link--last"
        aria-label="Last page"
        [disabled]="loading || currentPage >= totalPages"
        (click)="onLast()"
      >
        Last
      </button>
    </div>
  </div>
</div>
